import {sendGetRequest, sendRequest} from "../utils/http/http";
import {Api} from "../utils/http/api";
import {UpdateJwtToken} from "./global";
import {Appointment, defaultPatientInfo, PatientInfo} from "./types";
import {
    dataForEthnicity,
    dataForGender,
    dataForMartialStatus,
    dataForNotificationPreference,
    dataForPreferenceLanguage,
    dataForRace,
    dataForState,
} from "./datasource";
import { mPrint } from '../utils/mPrint'

export const sendAuth = (completeHandler: (isAuth: boolean) => void) => {
    sendGetRequest(Api.auth, (data) => {
        if (data && data.token && data.token.length) {
            UpdateJwtToken(data.token)
            completeHandler(true)
        } else {
            completeHandler(false)
        }
    }, () => {
        completeHandler(false)
    })
}

export const getAppointmentInfoByToken = (token: string, completeHandler: (tokenInfo?: PatientInfo | any) => void) => {
    const url = `${Api.appointmentInfo}${token}`
    sendGetRequest(url, (data) => {
        if (data && data.message !== "Expired") {
            const obj = {
                clinicID: data.clinicID || data.clinicid || 0,
                patientID: data.patientID || data.patientid || 0,
                updateID: data.updateID || data.updateid || 0,
                name: data.name,
                doctorName: data.doctorName,
                scheduleTime: data.scheduleTime,
                locationID: data.locationID || data.locationid || 0,
            }
            completeHandler(obj)
        } else {
            completeHandler(undefined)
        }
    }, () => {
        completeHandler(undefined)
    })
}

const formatObject = (info: PatientInfo | any) => {
    info.patientGender = info.patientGender && info.patientGender.trim()
    info.patientFirstName = info.patientFirstName && info.patientFirstName.trim()
    info.patientLastName = info.patientLastName && info.patientLastName.trim()
    info.patientAddrCity = info.patientAddrCity && info.patientAddrCity.trim()
    info.patientAddrZip = info.patientAddrZip && info.patientAddrZip.trim()
    if (info.patientDOB && info.patientDOB.length >= 10) {
        info.patientDOB = info.patientDOB.slice(0, 10)
    }

    const stateItem = dataForState.find((item) => {
        return item.value === info.patientAddrState
    })
    info.patientAddrState = stateItem ? stateItem : dataForState[0]

    const marryItem = dataForMartialStatus.find((item) => {
        return item.value === info.patientMarrage
    })
    info.patientMarrage = marryItem ? marryItem : dataForMartialStatus[0]

    const raceItem = dataForRace.find((item) => {
        return item.name === info.race
    })
    info.race = raceItem ? raceItem : dataForRace[0]

    const ethnicityItem = dataForEthnicity.find((item) => {
        return item.name === info.ethnicity
    })
    info.ethnicity = ethnicityItem ? ethnicityItem : dataForEthnicity[0]

    const genderItem = dataForGender.find((item) => {
        return item.value === info.patientGender
    })
    info.patientGender = genderItem ? genderItem : dataForGender[0]

    const contactMethodItem = dataForNotificationPreference.find((item) => {
        return item.value === info.contactMethod
    })
    info.contactMethod = contactMethodItem ? contactMethodItem : dataForNotificationPreference[0]

    const languageItem = dataForPreferenceLanguage.find((item) => {
        return item.value === info.language
    })
    info.language = languageItem ? languageItem : dataForPreferenceLanguage[0]

    return info
}

const getUpdatedPatientInfo = (patientID: number, clinicID: number, updateID: number, completeHandler: (data: PatientInfo | undefined) => void) => {
    const param = {
        "UpdateID": updateID,
        "ClinicID": clinicID,
        "PatientID": patientID,
    }
    sendRequest(Api.getUpdatePatient3, param, (data) => {
        if (data && data.result && data.result.result) {
            completeHandler(data.result.obj)
        } else {
            completeHandler(undefined)
        }
    }, () => {
        completeHandler(undefined)
    })
}

export const getPatientInfo = (token: string, completeHandler: (isSuccess: boolean, info: PatientInfo | undefined, appointment?: Appointment, msg?: string) => void) => {
    sendAuth((isAuth) => {
        if (isAuth) {
            getAppointmentInfoByToken(token, (tokenInfo) => {
                mPrint("tokenInfo: ", tokenInfo)
                if (!tokenInfo) {
                    completeHandler(false, undefined, undefined, "Link is invalid")
                    return
                }
                const {clinicID, patientID, updateID, name, doctorName, scheduleTime, locationID} = tokenInfo
                const appointment: Appointment = {
                    appointmentDate: scheduleTime,
                    clinicName: "",
                    clinicPhone: "",
                    doctorName: doctorName ? "Dr. " + doctorName.trim() : "",
                    isOfficeVisit: true,
                    locationID: locationID,
                    address: "",
                }
                if (patientID > 0) {
                    getUpdatedPatientInfo(patientID, clinicID, updateID, (data) => {
                        mPrint("existing patient ", data)
                        if (data) {
                            const fInfo = formatObject(data)
                            fInfo.clinicID = clinicID
                            fInfo.name = fInfo.patientFirstName + " " + fInfo.patientLastName
                            completeHandler(true, fInfo, appointment)
                        } else {
                            completeHandler(false, undefined, undefined)
                        }
                    })
                } else if (patientID === 0) {
                    mPrint("new patient")
                    let firstName = "", lastName = "", fullName = ""
                    if (name && name.length) {
                        fullName = name
                        const names = name.split(" ")
                        if (names.length >= 2) {
                            firstName = names[0]
                            lastName = names[1]
                        }
                    }
                    const newPatientInfo = Object.assign(defaultPatientInfo, {name: fullName, patientFirstName: firstName, patientLastName: lastName})
                    completeHandler(true, newPatientInfo)
                } else {
                    completeHandler(false, undefined, undefined)
                }
            })
        } else {
            completeHandler(false, undefined, undefined, "auth fail")
        }
    })
}

export const updatePatientInfo = (patientInfo: PatientInfo, skipInsurance: boolean, completeHandler: (isSuccess: boolean) => void) => {
    let param: any = {
        "UpdateID": patientInfo.updateID,
        "PatientID": patientInfo.patientID,
        "ClinicID": patientInfo.clinicID,
        "PatientAddrStreet": patientInfo.patientAddrStreet,
        "PatientAddrStreet2": patientInfo.patientAddrStreet2,
        "PatientAddrCity": patientInfo.patientAddrCity,
        "PatientAddrState": patientInfo.patientAddrState.value,
        "PatientAddrZip": patientInfo.patientAddrZip,
        "PatientMobilePhone": patientInfo.patientMobilePhone,
        "PatientEMail": patientInfo.patientEMail,
        "ContactMethod": patientInfo.contactMethod.value,
        "PatientName": patientInfo.name,
        "FirstName": patientInfo.patientFirstName,
        "LastName": patientInfo.patientLastName,
        "DOB": patientInfo.patientDOB,
        "Gender": patientInfo.patientGender.value,
        "PatientMarrage": patientInfo.patientMarrage.value,
        "Race": patientInfo.race.name,
        "RaceCode": patientInfo.race.raceCode,
        "Ethnicity": patientInfo.ethnicity.name,
        "EthnicityCode": patientInfo.ethnicity.ethnicityCode,
        "Language": patientInfo.language.value,
    }
    if (!skipInsurance && patientInfo.insuranceCardBackBase64 && patientInfo.insuranceCardFrontBase64) {
        param.InsuranceCardFrontStr = patientInfo.insuranceCardBackBase64.split(",").pop()
        param.InsuranceCardBackStr = patientInfo.insuranceCardFrontBase64.split(",").pop()
    }

    sendRequest(Api.updatePatient2, param, () => {
        completeHandler(true)
    }, () => {
        completeHandler(false)
    })
}

export const getOfficeLocationInfo = (locationID: number, completeHandler: (data: any) => void) => {
    const param = {
        "LocationID": locationID,
    }
    sendRequest(Api.getLocationById, param, (data) => {
        if (data && data.result) {
            completeHandler(data.result)
        } else {
            completeHandler(undefined)
        }
    }, () => {
        completeHandler(undefined)
    })
}

export const savePatientSignature = (patientInfo: PatientInfo, signature: string, completeHandler: (isSuccess: boolean) => void) => {
    const param = {
        SignStr: signature,
        PatientID: patientInfo.patientID,
        AppointmentID: patientInfo.appointmentID,
        UpdateID: 0,
        DOB: patientInfo.patientDOB,
        Gender: patientInfo.patientGender.value,
        "FirstName": patientInfo.patientFirstName,
        "MiddleName": "",
        "LastName": patientInfo.patientLastName,
        "appointment": "Y",
        "seedoctor": "N",
        "tokenID": patientInfo.tokenID,
        "language": "EN",
    }
    sendRequest(Api.signatureFinishApi, param, (data) => {
        if (data.result) {
            completeHandler(true)
        } else {
            completeHandler(false)
        }
    }, () => {
        completeHandler(false)
    })
}

export const checkin = (token: string, complete: (successful: boolean) => void) => {
    const url = `${Api.checkin}${token}`
    sendGetRequest(url, (data) => {
        complete(data?.result)
    }, () => {
        complete(false)
    })
}

export const getCheckInStatus = (token: string, complete: (checkedIn: boolean) => void) => {
    const url = `${Api.getCheckInStatus}${token}`
    sendGetRequest(url, (data) => {
        complete(data?.result && data?.confirmStatus === 1)
    }, () => {
        complete(false)
    })
}
